import type { FxSource, Transaction } from '../types';
import { resolveRatesForDate, todayIso } from './fxRates';

export { todayIso };

/**
 * FX capture for a transaction entry.
 *
 * The app stores prices in USD because ~26 files read `Transaction.price`, but
 * USD is a derived cache, not what the user actually did. This module records
 * what they actually did: the amount they typed, the currency they typed it in,
 * and the USD *and* AUD rates for one shared date.
 *
 * Both legs matter and neither derives from the other:
 *   - `fxRateUsd` keeps the portfolio (all audiences) correct.
 *   - `fxRateAud` keeps the Australian CGT report correct.
 * Deriving AUD from the stored USD would push every non-USD entry through a
 * second conversion at a second date, which is the error this model exists to
 * remove.
 *
 * New entries resolve their rates for the TRANSACTION'S OWN DATE via
 * `captureEntryFxForDate` / `captureTransferFxForDate` (see `./fxRates`), and
 * are tagged `rba`, `ecb` or `manual` accordingly. The `spot-at-entry` path
 * below is now only a FALLBACK, for when no date-correct rate can be found at
 * all — an unresolvable date is still worth recording the entry pair for,
 * because the backfill can revisit it later.
 */

/** The FX fields of a Transaction, as a standalone bag. */
export type FxCapture = Pick<
  Transaction,
  'entryAmount' | 'entryCurrency' | 'fxRateUsd' | 'fxRateAud' | 'fxRateDate' | 'fxSource'
>;

function usable(n: number | undefined): n is number {
  return typeof n === 'number' && isFinite(n) && n > 0;
}

/**
 * A successful rate resolution. `fxRateUsd` is always present — if it could not
 * be computed, `resolveSpotRates` returns null instead. `fxRateAud` is optional
 * because the AUD rate can be missing on its own (the feed has not loaded, or
 * does not carry AUD) without invalidating the USD leg.
 */
export interface ResolvedRates {
  fxRateUsd: number;
  fxRateAud?: number;
  fxRateDate: string;
  fxSource: FxSource;
}

/**
 * Resolve the USD and AUD legs for `entryCurrency` from a USD-based rate map
 * (`rates[X]` = units of X per 1 USD, which is what `useExchangeRates` returns).
 *
 * Returns null when the rate is missing or nonsensical, so callers record the
 * entry pair WITHOUT rates rather than writing a fabricated 1.0. A missing rate
 * is recoverable later; a wrong one recorded as fact is not.
 */
export function resolveSpotRates(
  entryCurrency: string,
  rates: Record<string, number>,
  now: Date = new Date()
): ResolvedRates | null {
  const code = (entryCurrency || '').toUpperCase();
  if (!code) return null;

  // Units of `code` per 1 USD. USD itself is exactly 1 and needs no lookup, so a
  // USD entry still captures cleanly even before the rate fetch resolves.
  const perUsd = code === 'USD' ? 1 : rates[code];
  const audPerUsd = rates.AUD;
  if (!usable(perUsd)) return null;

  // USD per 1 unit of entryCurrency.
  const fxRateUsd = 1 / perUsd;
  // AUD per 1 unit of entryCurrency, via USD: (AUD per USD) / (code per USD).
  const fxRateAud = code === 'AUD' ? 1 : usable(audPerUsd) ? audPerUsd / perUsd : undefined;

  return {
    fxRateUsd,
    ...(fxRateAud != null ? { fxRateAud } : {}),
    // The date these rates are genuinely FOR. They came from the live feed just
    // now, so that date is today — deliberately NOT the transaction date, which
    // would claim a date-correct rate we do not have. `fxSource` says the same
    // thing; recording both means a later backfill can spot the mismatch
    // without relying on the tag alone.
    fxRateDate: todayIso(now),
    fxSource: 'spot-at-entry',
  };
}

/**
 * Full capture for a newly entered price.
 *
 * `entryAmount` and `entryCurrency` are always recorded — they are observed
 * facts. The rates are recorded only if they resolve.
 */
export function captureEntryFx(
  entryAmount: number,
  entryCurrency: string,
  rates: Record<string, number>,
  now: Date = new Date()
): FxCapture {
  const resolved = resolveSpotRates(entryCurrency, rates, now);
  return {
    entryAmount,
    entryCurrency: (entryCurrency || '').toUpperCase(),
    ...(resolved ?? {}),
  };
}

/**
 * Capture for a TRANSFER, which has no price per unit but whose optional
 * network fee IS denominated in the entry currency.
 *
 * `entryAmount` is deliberately omitted (there is no price). Recording the
 * currency and rates still makes the stored fee recoverable later:
 * `rawFee = networkFee / fxRateUsd`.
 */
export function captureTransferFx(
  entryCurrency: string,
  rates: Record<string, number>,
  now: Date = new Date()
): FxCapture {
  const resolved = resolveSpotRates(entryCurrency, rates, now);
  return {
    entryCurrency: (entryCurrency || '').toUpperCase(),
    ...(resolved ?? {}),
  };
}

// ── Date-correct capture (the path new entries take) ─────────────────────────

/**
 * A capture together with the USD price it implies.
 *
 * The two travel together on purpose. `Transaction.price` is a DERIVED CACHE of
 * `entryAmount * fxRateUsd`, so a rate and a price that were computed from
 * different rates are an inconsistency waiting to surface as a penny of drift in
 * the portfolio. Resolving a date-correct rate therefore RECOMPUTES the stored
 * USD price from that same rate, in the same step — the caller cannot apply one
 * without the other.
 *
 * `priceUsd` is null only when no rate resolved at all (so there is nothing to
 * derive it from); callers fall back to the display-rate conversion.
 */
export interface PricedFxCapture {
  fx: FxCapture;
  priceUsd: number | null;
}

/**
 * Full capture for a newly entered price, resolved for the transaction's OWN
 * date rather than today.
 *
 * Chain: RBA → Frankfurter (ECB) → today's spot as a last resort. The spot
 * fallback is still tagged `spot-at-entry`, so it stays visible as provisional
 * and the backfill can find it later.
 */
export async function captureEntryFxForDate(
  entryAmount: number,
  entryCurrency: string,
  date: string,
  spotRates: Record<string, number>,
  now: Date = new Date()
): Promise<PricedFxCapture> {
  const code = (entryCurrency || '').toUpperCase();
  const resolved = (await resolveRatesForDate(code, date)) ?? resolveSpotRates(code, spotRates, now);

  return {
    fx: compactFx({ entryAmount, entryCurrency: code, ...(resolved ?? {}) }),
    // The invariant: price === entryAmount × fxRateUsd, always from the same rate
    // that was just stored.
    priceUsd: resolved ? entryAmount * resolved.fxRateUsd : null,
  };
}

/**
 * Date-correct capture for a TRANSFER, which has no price per unit but whose
 * optional network fee IS denominated in the entry currency.
 *
 * `priceUsd` here converts the FEE, not a price — a transfer's `price` is always
 * 0. Callers pass the fee as `feeAmount` and store the returned value as
 * `networkFee`, keeping `networkFee = rawFee × fxRateUsd` on the same rate.
 */
export async function captureTransferFxForDate(
  entryCurrency: string,
  date: string,
  spotRates: Record<string, number>,
  feeAmount = 0,
  now: Date = new Date()
): Promise<PricedFxCapture> {
  const code = (entryCurrency || '').toUpperCase();
  const resolved = (await resolveRatesForDate(code, date)) ?? resolveSpotRates(code, spotRates, now);

  return {
    // No entryAmount: a transfer has no price per unit. The currency and rates
    // are still enough to recover the fee as typed: rawFee = networkFee / fxRateUsd.
    fx: compactFx({ entryCurrency: code, ...(resolved ?? {}) }),
    priceUsd: resolved ? feeAmount * resolved.fxRateUsd : null,
  };
}

// ── Transaction fee capture (BUY / SELL) ─────────────────────────────────────

/** The fee fields of a Transaction, as a standalone bag. */
export type FeeCapture = Pick<Transaction, 'fee' | 'feeCurrency' | 'feeUsd' | 'feeAud'>;

/**
 * Capture a BUY/SELL fee for the transaction's own date.
 *
 * `fee` and `feeCurrency` are recorded RAW and are never replaced by a converted
 * value — that is what keeps the conversion re-resolvable if a better rate turns
 * up later. `feeUsd` / `feeAud` are derived caches alongside them, from the rate
 * for `date`, falling back to today's spot only if the date resolves to nothing.
 *
 * A zero or absent fee records nothing at all, so a row without a fee is
 * indistinguishable from one created before fees existed — which is what makes
 * "no fee means no change" hold.
 */
export async function captureFeeForDate(
  fee: number,
  feeCurrency: string,
  date: string,
  spotRates: Record<string, number>,
  now: Date = new Date()
): Promise<FeeCapture> {
  if (!(fee > 0) || !isFinite(fee)) return {};

  const code = (feeCurrency || '').toUpperCase();
  if (!code) return {};

  const resolved = (await resolveRatesForDate(code, date)) ?? resolveSpotRates(code, spotRates, now);

  return compactFee({
    fee,
    feeCurrency: code,
    ...(resolved
      ? {
          feeUsd: fee * resolved.fxRateUsd,
          ...(resolved.fxRateAud != null ? { feeAud: fee * resolved.fxRateAud } : {}),
        }
      : {}),
  });
}

/** Drop undefined keys, so optional fee fields are absent rather than undefined. */
export function compactFee(f: FeeCapture): FeeCapture {
  const out: FeeCapture = {};
  if (f.fee != null) out.fee = f.fee;
  if (f.feeCurrency != null) out.feeCurrency = f.feeCurrency;
  if (f.feeUsd != null) out.feeUsd = f.feeUsd;
  if (f.feeAud != null) out.feeAud = f.feeAud;
  return out;
}

/** The stored fee fields of an existing transaction, preserved verbatim. */
export function preserveFee(tx: Transaction): FeeCapture {
  return compactFee({
    fee: tx.fee,
    feeCurrency: tx.feeCurrency,
    feeUsd: tx.feeUsd,
    feeAud: tx.feeAud,
  });
}

/** The stored rate fields of an existing transaction, preserved verbatim. */
export function preserveRates(
  tx: Pick<Transaction, 'fxRateUsd' | 'fxRateAud' | 'fxRateDate' | 'fxSource'>
): Pick<FxCapture, 'fxRateUsd' | 'fxRateAud' | 'fxRateDate' | 'fxSource'> {
  return {
    ...(tx.fxRateUsd != null ? { fxRateUsd: tx.fxRateUsd } : {}),
    ...(tx.fxRateAud != null ? { fxRateAud: tx.fxRateAud } : {}),
    ...(tx.fxRateDate != null ? { fxRateDate: tx.fxRateDate } : {}),
    ...(tx.fxSource != null ? { fxSource: tx.fxSource } : {}),
  };
}

/** Drop undefined keys, so optional fields are absent rather than explicitly undefined. */
export function compactFx(fx: FxCapture): FxCapture {
  const out: FxCapture = {};
  if (fx.entryAmount != null) out.entryAmount = fx.entryAmount;
  if (fx.entryCurrency != null) out.entryCurrency = fx.entryCurrency;
  if (fx.fxRateUsd != null) out.fxRateUsd = fx.fxRateUsd;
  if (fx.fxRateAud != null) out.fxRateAud = fx.fxRateAud;
  if (fx.fxRateDate != null) out.fxRateDate = fx.fxRateDate;
  if (fx.fxSource != null) out.fxSource = fx.fxSource;
  return out;
}
